import React, { useState } from 'react';
import { SearchIcon, PlusIcon, FileTextIcon, CalendarIcon, DollarSignIcon, CheckCircleIcon, ClockIcon, XIcon, AlertTriangleIcon } from 'lucide-react';
export function ContractVariations({
  locale
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [formData, setFormData] = useState({
    contractId: '',
    variationNumber: '',
    description: '',
    reason: '',
    requestDate: '',
    approvalDate: '',
    effectiveDate: '',
    originalValue: '',
    variationValue: '',
    status: 'Draft',
    documents: []
  });
  // Sample variations data
  const variations = [{
    id: 'VAR-2023-001',
    contractId: 'CTR-2023-002',
    contractName: 'IT Support Services',
    vendor: 'TechPro Solutions',
    variationNumber: '1',
    description: 'Expanded service coverage to include cloud infrastructure',
    reason: 'Business requirements changed',
    requestDate: '2023-06-15',
    approvalDate: '2023-06-28',
    effectiveDate: '2023-07-01',
    originalValue: 120000,
    variationValue: 35000,
    status: 'Approved'
  }, {
    id: 'VAR-2023-002',
    contractId: 'CTR-2023-001',
    contractName: 'Office Lease Agreement',
    vendor: 'Skyline Properties Inc.',
    variationNumber: '1',
    description: 'Additional office space on 3rd floor',
    reason: 'Company expansion',
    requestDate: '2023-05-10',
    approvalDate: '',
    effectiveDate: '',
    originalValue: 240000,
    variationValue: 60000,
    status: 'Pending Approval'
  }, {
    id: 'VAR-2023-003',
    contractId: 'CTR-2023-006',
    contractName: 'Consulting Services',
    vendor: 'Business Consultants Ltd.',
    variationNumber: '1',
    description: 'Reduced scope of strategic planning services',
    reason: 'Budget constraints',
    requestDate: '2023-07-05',
    approvalDate: '',
    effectiveDate: '',
    originalValue: 95000,
    variationValue: -25000,
    status: 'Draft'
  }, {
    id: 'VAR-2023-004',
    contractId: 'CTR-2023-008',
    contractName: 'Security Services',
    vendor: 'SecureGuard Inc.',
    variationNumber: '1',
    description: 'Additional security personnel for special event',
    reason: 'One-time company event',
    requestDate: '2023-06-20',
    approvalDate: '2023-06-25',
    effectiveDate: '2023-08-15',
    originalValue: 75000,
    variationValue: 5000,
    status: 'Approved'
  }, {
    id: 'VAR-2023-005',
    contractId: 'CTR-2023-010',
    contractName: 'Cloud Infrastructure',
    vendor: 'AWS',
    variationNumber: '1',
    description: 'Increased storage and compute resources',
    reason: 'Growth in data processing needs',
    requestDate: '2023-07-10',
    approvalDate: '',
    effectiveDate: '',
    originalValue: 180000,
    variationValue: 45000,
    status: 'Rejected'
  }];
  // Filter variations based on search term
  const filteredVariations = variations.filter(variation => variation.id.toLowerCase().includes(searchTerm.toLowerCase()) || variation.contractId.toLowerCase().includes(searchTerm.toLowerCase()) || variation.contractName.toLowerCase().includes(searchTerm.toLowerCase()) || variation.vendor.toLowerCase().includes(searchTerm.toLowerCase()) || variation.description.toLowerCase().includes(searchTerm.toLowerCase()));
  // Format currency
  const formatCurrency = value => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };
  // Get status badge color
  const getStatusColor = status => {
    switch (status) {
      case 'Approved':
        return 'bg-green-100 text-green-800';
      case 'Pending Approval':
        return 'bg-yellow-100 text-yellow-800';
      case 'Draft':
        return 'bg-gray-100 text-gray-800';
      case 'Rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  // Get status icon
  const getStatusIcon = status => {
    switch (status) {
      case 'Approved':
        return CheckCircleIcon;
      case 'Pending Approval':
        return ClockIcon;
      case 'Draft':
        return FileTextIcon;
      case 'Rejected':
        return XIcon;
      default:
        return AlertTriangleIcon;
    }
  };
  const handleChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  const handleSubmit = e => {
    e.preventDefault();
    // In a real app, this would save the variation to the database
    console.log('Saving variation:', formData);
    // Show success message
    alert(`Variation saved successfully!`);
    // Reset form and view
    setShowForm(false);
    setSelectedVariation(null);
  };
  const handleAddNew = () => {
    setFormData({
      contractId: '',
      variationNumber: '',
      description: '',
      reason: '',
      requestDate: '',
      approvalDate: '',
      effectiveDate: '',
      originalValue: '',
      variationValue: '',
      status: 'Draft',
      documents: []
    });
    setSelectedVariation(null);
    setShowForm(true);
  };
  const handleEdit = variation => {
    setFormData({
      contractId: variation.contractId,
      variationNumber: variation.variationNumber,
      description: variation.description,
      reason: variation.reason,
      requestDate: variation.requestDate,
      approvalDate: variation.approvalDate,
      effectiveDate: variation.effectiveDate,
      originalValue: variation.originalValue.toString(),
      variationValue: variation.variationValue.toString(),
      status: variation.status,
      documents: []
    });
    setSelectedVariation(variation);
    setShowForm(true);
  };
  return <div data-id="element-2952">
      <div className="mb-8 flex justify-between items-center" data-id="element-2953">
        <div data-id="element-2954">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-2955">
            Contract Variations
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-2956">
            Manage changes to existing contracts
          </p>
        </div>
        <button onClick={handleAddNew} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none" data-id="element-2957">
          <PlusIcon className="h-4 w-4 mr-2" data-id="element-2958" />
          Add Variation
        </button>
      </div>
      {showForm ? <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-2959">
          <form onSubmit={handleSubmit} className="p-6" data-id="element-2960">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-2961">
              {/* Contract Selection */}
              <div className="sm:col-span-3" data-id="element-2962">
                <label htmlFor="contractId" className="block text-sm font-medium text-gray-700" data-id="element-2963">
                  Contract
                </label>
                <div className="mt-1" data-id="element-2964">
                  <select id="contractId" name="contractId" value={formData.contractId} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2965">
                    <option value="" data-id="element-2966">Select a contract</option>
                    <option value="CTR-2023-001" data-id="element-2967">
                      CTR-2023-001 - Office Lease Agreement
                    </option>
                    <option value="CTR-2023-002" data-id="element-2968">
                      CTR-2023-002 - IT Support Services
                    </option>
                    <option value="CTR-2023-006" data-id="element-2969">
                      CTR-2023-006 - Consulting Services
                    </option>
                    <option value="CTR-2023-008" data-id="element-2970">
                      CTR-2023-008 - Security Services
                    </option>
                    <option value="CTR-2023-010" data-id="element-2971">
                      CTR-2023-010 - Cloud Infrastructure
                    </option>
                  </select>
                </div>
              </div>
              {/* Variation Number */}
              <div className="sm:col-span-3" data-id="element-2972">
                <label htmlFor="variationNumber" className="block text-sm font-medium text-gray-700" data-id="element-2973">
                  Variation Number
                </label>
                <div className="mt-1" data-id="element-2974">
                  <input type="text" name="variationNumber" id="variationNumber" value={formData.variationNumber} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2975" />
                </div>
              </div>
              {/* Description */}
              <div className="sm:col-span-6" data-id="element-2976">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-2977">
                  Description
                </label>
                <div className="mt-1" data-id="element-2978">
                  <textarea id="description" name="description" rows={2} value={formData.description} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2979" />
                </div>
              </div>
              {/* Reason */}
              <div className="sm:col-span-6" data-id="element-2980">
                <label htmlFor="reason" className="block text-sm font-medium text-gray-700" data-id="element-2981">
                  Reason for Variation
                </label>
                <div className="mt-1" data-id="element-2982">
                  <textarea id="reason" name="reason" rows={2} value={formData.reason} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2983" />
                </div>
              </div>
              {/* Request Date */}
              <div className="sm:col-span-2" data-id="element-2984">
                <label htmlFor="requestDate" className="block text-sm font-medium text-gray-700" data-id="element-2985">
                  Request Date
                </label>
                <div className="mt-1" data-id="element-2986">
                  <input type="date" name="requestDate" id="requestDate" value={formData.requestDate} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2987" />
                </div>
              </div>
              {/* Approval Date */}
              <div className="sm:col-span-2" data-id="element-2988">
                <label htmlFor="approvalDate" className="block text-sm font-medium text-gray-700" data-id="element-2989">
                  Approval Date
                </label>
                <div className="mt-1" data-id="element-2990">
                  <input type="date" name="approvalDate" id="approvalDate" value={formData.approvalDate} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2991" />
                </div>
              </div>
              {/* Effective Date */}
              <div className="sm:col-span-2" data-id="element-2992">
                <label htmlFor="effectiveDate" className="block text-sm font-medium text-gray-700" data-id="element-2993">
                  Effective Date
                </label>
                <div className="mt-1" data-id="element-2994">
                  <input type="date" name="effectiveDate" id="effectiveDate" value={formData.effectiveDate} onChange={handleChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-2995" />
                </div>
              </div>
              {/* Original Value */}
              <div className="sm:col-span-2" data-id="element-2996">
                <label htmlFor="originalValue" className="block text-sm font-medium text-gray-700" data-id="element-2997">
                  Original Contract Value
                </label>
                <div className="mt-1 relative rounded-md shadow-sm" data-id="element-2998">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-2999">
                    <DollarSignIcon className="h-5 w-5 text-gray-400" data-id="element-3000" />
                  </div>
                  <input type="number" name="originalValue" id="originalValue" value={formData.originalValue} onChange={handleChange} required className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="0.00" data-id="element-3001" />
                </div>
              </div>
              {/* Variation Value */}
              <div className="sm:col-span-2" data-id="element-3002">
                <label htmlFor="variationValue" className="block text-sm font-medium text-gray-700" data-id="element-3003">
                  Variation Value
                </label>
                <div className="mt-1 relative rounded-md shadow-sm" data-id="element-3004">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3005">
                    <DollarSignIcon className="h-5 w-5 text-gray-400" data-id="element-3006" />
                  </div>
                  <input type="number" name="variationValue" id="variationValue" value={formData.variationValue} onChange={handleChange} required className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="0.00" data-id="element-3007" />
                </div>
                <p className="mt-1 text-xs text-gray-500" data-id="element-3008">
                  Use negative values for reductions
                </p>
              </div>
              {/* Status */}
              <div className="sm:col-span-2" data-id="element-3009">
                <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-3010">
                  Status
                </label>
                <div className="mt-1" data-id="element-3011">
                  <select id="status" name="status" value={formData.status} onChange={handleChange} required className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-3012">
                    <option value="Draft" data-id="element-3013">Draft</option>
                    <option value="Pending Approval" data-id="element-3014">Pending Approval</option>
                    <option value="Approved" data-id="element-3015">Approved</option>
                    <option value="Rejected" data-id="element-3016">Rejected</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="pt-5 mt-6 border-t border-gray-200" data-id="element-3017">
              <div className="flex justify-end" data-id="element-3018">
                <button type="button" onClick={() => setShowForm(false)} className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none" data-id="element-3019">
                  Cancel
                </button>
                <button type="submit" className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none" data-id="element-3020">
                  {selectedVariation ? 'Update Variation' : 'Create Variation'}
                </button>
              </div>
            </div>
          </form>
        </div> : <>
          {/* Search */}
          <div className="mb-6" data-id="element-3021">
            <div className="max-w-md" data-id="element-3022">
              <div className="relative rounded-md shadow-sm" data-id="element-3023">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3024">
                  <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-3025" />
                </div>
                <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search variations..." data-id="element-3026" />
              </div>
            </div>
          </div>
          {/* Variations Table */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-3027">
            <div className="overflow-x-auto" data-id="element-3028">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-3029">
                <thead className="bg-gray-50" data-id="element-3030">
                  <tr data-id="element-3031">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3032">
                      Variation
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3033">
                      Contract
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3034">
                      Description
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3035">
                      Dates
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3036">
                      Value
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3037">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3038">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-3039">
                  {filteredVariations.length === 0 ? <tr data-id="element-3040">
                      <td colSpan={7} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-3041">
                        No variations found matching your criteria.
                      </td>
                    </tr> : filteredVariations.map(variation => {
                const StatusIcon = getStatusIcon(variation.status);
                return <tr key={variation.id} className="hover:bg-gray-50" data-id="element-3042">
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-3043">
                            <div className="flex items-center" data-id="element-3044">
                              <div data-id="element-3045">
                                <div className="text-sm font-medium text-indigo-600" data-id="element-3046">
                                  {variation.id}
                                </div>
                                <div className="text-sm text-gray-500" data-id="element-3047">
                                  Variation #{variation.variationNumber}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-3048">
                            <div className="text-sm text-gray-900" data-id="element-3049">
                              {variation.contractId}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-3050">
                              {variation.contractName}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-3051">
                              {variation.vendor}
                            </div>
                          </td>
                          <td className="px-6 py-4" data-id="element-3052">
                            <div className="text-sm text-gray-900 max-w-xs truncate" data-id="element-3053">
                              {variation.description}
                            </div>
                            <div className="text-xs text-gray-500 max-w-xs truncate" data-id="element-3054">
                              Reason: {variation.reason}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3055">
                            <div className="flex items-center" data-id="element-3056">
                              <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-3057" />
                              <span data-id="element-3058">Requested: {variation.requestDate}</span>
                            </div>
                            {variation.approvalDate && <div className="flex items-center mt-1" data-id="element-3059">
                                <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" data-id="element-3060" />
                                <span data-id="element-3061">Approved: {variation.approvalDate}</span>
                              </div>}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-3062">
                            <div className="text-sm text-gray-900" data-id="element-3063">
                              {formatCurrency(variation.variationValue)}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-3064">
                              Original:{' '}
                              {formatCurrency(variation.originalValue)}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-3065">
                              New:{' '}
                              {formatCurrency(variation.originalValue + variation.variationValue)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap" data-id="element-3066">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(variation.status)}`} data-id="element-3067">
                              <StatusIcon className="h-3 w-3 mr-1" data-id="element-3068" />
                              {variation.status}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-3069">
                            <button onClick={() => handleEdit(variation)} className="text-indigo-600 hover:text-indigo-900" data-id="element-3070">
                              Edit
                            </button>
                          </td>
                        </tr>;
              })}
                </tbody>
              </table>
            </div>
          </div>
        </>}
    </div>;
}